import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  updateProfile,
  updateEmail,
  signOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, GeoPoint } from "firebase/firestore";
import { auth, db } from "../firebase/firebase-config";

const buildProvider = (kind) => {
  if (kind === "google") {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return provider;
  }
  const provider = new FacebookAuthProvider();
  provider.addScope("public_profile");
  provider.addScope("email");
  return provider;
};

// Same shape the phone signup writes, so a social account can buy tickets,
// become a host, and list a venue without a half-empty profile.
export const buildNewUserDoc = ({ uid, displayName, email, avatarUrl }) => ({
  accountId: null,
  avatarUrl: avatarUrl || null,
  bio: "",
  city: "",
  country: null,
  customerId: "",
  dateOfBirth: null,
  displayName,
  email: email || "",
  friends: [],
  gender: null,
  hasOrg: false,
  id: uid,
  igAccess: false,
  igToken: null,
  igUserId: null,
  interests: [],
  isFinished: false,
  isHost: false,
  location: {
    geohash: "",
    geopoint: new GeoPoint(0, 0),
  },
  notificationSettings: null,
  origin: "web",
  pushToken: "",
  searchRadius: null,
  study: null,
  work: null,
});

const cleanName = (value) => {
  const name = String(value || "").replace(/\s+/g, " ").trim();
  if (!name || name.includes("@") || /^(null|undefined)$/i.test(name)) return "";
  return name;
};

const cleanEmail = (value) => {
  const email = String(value || "").trim().toLowerCase();
  return email.includes("@") ? email : "";
};

// Google photo URLs end in a size token (`=s96-c`). Ask for a sharper one
// so the avatar isn't a tiny thumbnail blown up in the UI.
const normalizeAvatar = (url) => {
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!/^https:\/\//i.test(trimmed)) return null;
  return trimmed.replace(/=s\d+(-c)?$/, "=s256-c");
};

const profileIsComplete = (profile) =>
  Boolean(profile.firstName && profile.lastName && profile.email);

// Pulls a first name, last name, email, and photo out of Google or Facebook.
// Structured fields win over splitting displayName, because "Mary Ann Smith"
// shouldn't lose a name. A Facebook default silhouette is not a real photo.
const profileFromResult = (user, result) => {
  const profile = getAdditionalUserInfo(result)?.profile || {};

  let firstName = cleanName(profile.given_name || profile.first_name);
  let lastName = cleanName(profile.family_name || profile.last_name);

  if (!firstName || !lastName) {
    const full = cleanName(
      user.displayName || (typeof profile.name === "string" ? profile.name : "")
    );
    if (full) {
      const parts = full.split(" ");
      firstName = firstName || parts[0] || "";
      lastName = lastName || parts.slice(1).join(" ");
    }
  }

  const pictureData =
    profile.picture && typeof profile.picture === "object" ? profile.picture.data : null;
  const rawPicture = pictureData?.is_silhouette
    ? null
    : pictureData?.url ||
      (typeof profile.picture === "string" ? profile.picture : null) ||
      user.photoURL ||
      null;

  return {
    firstName,
    lastName,
    email: cleanEmail(user.email || profile.email),
    avatarUrl: normalizeAvatar(rawPicture),
  };
};

// Writes the same user doc the phone signup writes. Phone accounts have no
// provider email, so a failed Auth email update has to stop them. Google and
// Facebook already proved the address, so a provider refusal still keeps the
// address on the Firestore profile, which is what ticket mail uses.
export const saveUserProfile = async (
  user,
  { firstName, lastName, email, avatarUrl, requireAuthEmail = false }
) => {
  const cleanedFirst = cleanName(firstName);
  const cleanedLast = cleanName(lastName);
  const displayName = cleanName(`${cleanedFirst} ${cleanedLast}`);
  const emailValue = cleanEmail(email);
  if (!cleanedFirst || !cleanedLast || !emailValue) {
    const error = new Error("Profile is incomplete");
    error.code = "auth/invalid-profile";
    throw error;
  }

  await updateProfile(user, {
    displayName,
    ...(avatarUrl ? { photoURL: avatarUrl } : {}),
  });

  if ((user.email || "").toLowerCase() !== emailValue) {
    try {
      await updateEmail(user, emailValue);
    } catch (error) {
      const providerKeptEmail =
        error.code === "auth/operation-not-allowed" ||
        error.code === "auth/requires-recent-login";
      if (requireAuthEmail || !providerKeptEmail) throw error;
      console.warn("Saved the email on the profile. Auth left its own email in place.", error);
    }
  }

  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  const payload = snap.exists()
    ? {
        id: user.uid,
        displayName,
        email: emailValue,
        avatarUrl: avatarUrl || snap.data()?.avatarUrl || null,
      }
    : buildNewUserDoc({
        uid: user.uid,
        displayName,
        email: emailValue,
        avatarUrl,
      });
  await setDoc(ref, payload, { merge: true });
};

// Closing the sign-in window after Google or Facebook succeeded, but before
// a profile exists, would leave them looking signed in with no account.
export const signOutIfProfileIncomplete = async () => {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const snap = await getDoc(doc(db, "users", user.uid));
    if (!cleanName(snap.data()?.displayName)) {
      await signOut(auth);
    }
  } catch (error) {
    console.warn("Couldn't check the profile before closing sign-in:", error);
  }
};

// Returning users with a profile are done. New users with a full name and
// email are written immediately. Anyone missing a piece goes to the name step
// with whatever the provider did send already filled in.
export const signInWithSocial = async (kind) => {
  const result = await signInWithPopup(auth, buildProvider(kind));
  const user = result.user;
  const profile = profileFromResult(user, result);
  const snap = await getDoc(doc(db, "users", user.uid));
  const existing = snap.exists() ? snap.data() : null;

  if (cleanName(existing?.displayName)) {
    const patch = {};
    if (profile.avatarUrl && !existing.avatarUrl) patch.avatarUrl = profile.avatarUrl;
    if (profile.email && !cleanEmail(existing.email)) patch.email = profile.email;
    if (Object.keys(patch).length) {
      await setDoc(doc(db, "users", user.uid), patch, { merge: true });
      if (patch.avatarUrl && !user.photoURL) {
        await updateProfile(user, { photoURL: patch.avatarUrl });
      }
    }
    return { complete: true, isNew: false };
  }

  if (profileIsComplete(profile)) {
    await saveUserProfile(user, profile);
    return { complete: true, isNew: true };
  }

  return { complete: false, isNew: true, ...profile };
};

export const socialErrorMessage = (error) => {
  switch (error.code) {
    case "auth/account-exists-with-different-credential":
      return "This email is already used with another sign-in method. Use the one you signed up with.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow popups and try again.";
    case "auth/unauthorized-domain":
      return "This site isn't authorized for that sign-in method yet.";
    case "auth/operation-not-allowed":
      return "That sign-in method isn't turned on yet.";
    default:
      return "Couldn't sign you in. Please try again.";
  }
};
